<template>
  <div class="l-stack l-gap-2 l-padded">
    <loading-overlay v-if="isLoading" />
    <div>
      <div
        class="title is-4 title-align-center"
        v-if="targetVariable === 'gdp'"
      >
        <ncl-flag :iso="country" />
        {{ countryName }} {{ targetPeriod }} {{ indicatorUpperCaseName }}
        {{ forecastType }}
      </div>
      <div class="title is-4" v-else>
        {{ countryName }} {{ targetPeriod }} {{ forecastType }}
      </div>
    </div>
    <div class="columns">
      <div class="level-item">
        <div class="column col-4">
          <span class="subheading"
            >Select model
            <span id="models-info">
              <ncl-info-icon
                text="Click for information on models"
                location="right"
              />
            </span>
          </span>
          <v-chip-group
            v-if="!$vuetify.display.smAndDown"
            column
            mandatory
            selected-class="text-primary"
            v-model="dataModel"
          >
            <v-chip
              v-for="(key, model) in dataModels"
              :key="key"
              :value="model"
            >
              {{ key }}
            </v-chip>
          </v-chip-group>
          <v-select
            v-else
            v-model="dataModel"
            :items="modelSelectItems"
            density="compact"
            variant="outlined"
            hide-details
            menu-icon="mdi-chevron-down"
            class="model-select-mobile"
          ></v-select>
        </div>
      </div>
    </div>
    <div class="columns is-multiline">
      <div class="column">
        <div class="box">
          <div class="level">
            <div class="level-item">
              <div class="column">
                <div class="heading">Forecast</div>
                <div class="title" v-if="currentForecast >= 0">
                  +{{ currentForecast }}%
                </div>
                <div class="title" v-else-if="currentForecast < 0">
                  {{ currentForecast }}%
                </div>
                <div class="title" v-else>
                  <span class="icon">
                    <v-icon class="title" icon="mdi-minus"></v-icon>
                  </span>
                </div>
              </div>
            </div>
            <div class="level-item">
              <ncl-info-icon :text="forecastTooltip" location="right" />
            </div>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="box">
          <div class="level">
            <div class="level-item">
              <div class="column">
                <div class="heading no-wrap">Lower Bound</div>
                <div class="title" v-if="currentLowerBound >= 0">
                  +{{ currentLowerBound }}%
                </div>
                <div class="title" v-else-if="currentLowerBound < 0">
                  {{ currentLowerBound }}%
                </div>
                <div class="title" v-else>
                  <span class="icon">
                    <v-icon class="title" icon="mdi-minus"></v-icon>
                  </span>
                </div>
              </div>
            </div>
            <div class="level-item">
              <ncl-info-icon
                text="Lower bound for the now-/forecast as measured by: now-/forecast - 1.28 * RMSFE. This corresponds to a two-sided 90% confidence interval."
                location="bottom"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="box">
          <div class="level">
            <div class="level-item">
              <div class="column">
                <div class="heading no-wrap">Upper Bound</div>
                <div class="title" v-if="currentUpperBound >= 0">
                  +{{ currentUpperBound }}%
                </div>
                <div class="title" v-else-if="currentUpperBound < 0">
                  {{ currentUpperBound }}%
                </div>
                <div class="title" v-else>
                  <span class="icon">
                    <v-icon class="title" icon="mdi-minus"></v-icon>
                  </span>
                </div>
              </div>
            </div>
            <div class="level-item">
              <ncl-info-icon
                text="Upper bound for the forecast as measured by: now-/forecast + 1.28 * RMSFE. This corresponds to a two-sided 90% confidence interval."
                location="bottom"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="box">
          <div class="level">
            <div class="level-item">
              <div class="column">
                <div class="heading">7-Day Trend</div>
                <div class="title">
                  <span class="icon">
                    <v-icon
                      class="title"
                      v-if="getCurrentTrend > 0"
                      icon="mdi-trending-up"
                    ></v-icon>
                    <v-icon
                      class="title"
                      v-if="getCurrentTrend === 0"
                      icon="mdi-trending-neutral"
                    ></v-icon>
                    <v-icon
                      class="title"
                      v-else-if="getCurrentTrend < 0"
                      icon="mdi-trending-down"
                    ></v-icon>
                    <v-icon
                      class="title"
                      v-else-if="!getCurrentTrend"
                      icon="mdi-minus"
                    ></v-icon>
                  </span>
                </div>
              </div>
            </div>
            <div class="level-item">
              <ncl-info-icon
                text="Development of the forecast as compared to 7 days ago. An arrow pointing upwards indicates an upward trend, an arrow pointing downwards indicates a downward trend, and an arrow pointing sideways indicates a sideways trend. During the first seven days of the forecast period, the trend is built by comparing the current forecast with the first forecast in the forecast period."
                location="bottom"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="box">
          <div class="level">
            <div class="level-item">
              <div class="column">
                <div class="heading">RMSFE</div>
                <div class="title" v-if="currentRMSFE">
                  {{ currentRMSFE }}pp
                </div>
                <div class="title" v-else>
                  <span class="icon">
                    <v-icon class="title" icon="mdi-minus"></v-icon>
                  </span>
                </div>
              </div>
            </div>
            <div class="level-item">
              <ncl-info-icon
                text="Now-/Forecast precision as measured by the root mean squared now-/forecast error based on the now-/forecasts of the past eight periods."
                location="bottom"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="box">
          <div class="level">
            <div class="level-item">
              <div class="column">
                <div class="heading">Bias</div>
                <div class="title" v-if="currentBias >= 0">
                  +{{ currentBias }}
                </div>
                <div class="title" v-else-if="currentBias < 0">
                  {{ currentBias }}
                </div>
                <div class="title" v-else>
                  <span class="icon">
                    <v-icon class="title" icon="mdi-minus"></v-icon>
                  </span>
                </div>
              </div>
            </div>
            <div class="level-item">
              <ncl-info-icon
                text="Bias as measured by the average of the now-/forecast errors of the past eight periods."
                location="left"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="columns">
        <div class="column is-three-fifths">
          <p class="subtitle is-4 title-align-center">Forecast History</p>
          <forecast-history
            :key="`${country}-${targetPeriod}-${targetVariable}-${model}`"
            @activedaytimechange="onCurrentDateTimeChange"
            @activecategorieschange="onActiveCategoriesChange"
            :country="country"
            :targetperiod="targetPeriod"
            :targetvariable="targetVariable"
            :indicator="this.$route.query.indicator"
            :model="model"
          />
        </div>
        <div class="column is-two-fifths">
          <p class="subtitle is-4 title-align-center">
            Forecast Distribution for {{ currentDayRobust }}
            <ncl-info-icon
              text='If model option "Bridge", "MIDAS" or "U-MIDAS" is selected, the forecast dirtribution chart shows all individual forecasts, which get pooled to produce one (pooled) forecast. If model option "DFM", "Random Walk", "Rolling Mean", "Iterative AR", "ARIMA", or "Direct AR" is selected, the chart is not applicable since no pooling of individual forecasts occurs for the dynamic factor model. If model option "Pooled" is selected, the forecast distribution chart shows the four forecasts resulting from the four different model classes.'
              location="bottom"
            />
          </p>
          <div
            v-if="
              [
                'dfm_excl_covid',
                'randomwalk',
                'rollmean',
                'ar_iterative_bic',
                'arima',
                'ar_direct_bic',
              ].includes(dataModel)
            "
            class="subtitle is-4"
          >
            <p class="text-not-applicable">Not applicable</p>
          </div>
          <highcharts
            v-else
            :options="histogramOptions"
            ref="histogramChart"
          ></highcharts>

          <p class="subtitle is-4 title-align-center">
            Data Releases for {{ currentDayRobust }}
          </p>
          <div class="container columns is-multiline">
            <div class="column text-center">
              <p style="font-size: 13px">Forecast before releases</p>
              <p v-if="indicatorValuePreviousDay">
                {{ indicatorValuePreviousDay }}
              </p>
              <v-icon v-else icon="mdi-minus"></v-icon>
            </div>
            <div class="column text-center">
              <p style="font-size: 13px">Forecast after releases</p>
              <p v-if="currentForecast">{{ currentForecast }}</p>
              <v-icon v-else icon="mdi-minus"></v-icon>
            </div>
            <div class="column text-center">
              <p style="font-size: 13px">Change in forecast</p>
              <p v-if="indicatorValueDifferenceToPreviousDay !== null">
                {{ indicatorValueDifferenceToPreviousDay }}
              </p>
              <v-icon v-else icon="mdi-minus"></v-icon>
            </div>
          </div>
          <release-table
            :active-categories="activeCategories"
            @row-click="tableRowClick"
          ></release-table>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="columns">
        <div class="column">
          <p class="subtitle is-4 title-align-center">
            {{ historicSeriesChartTitle }}
          </p>
          <highcharts
            :options="historicSeriesOptions"
            ref="historicSeriesChart"
          ></highcharts>
        </div>
      </div>
    </div>

    <!-- release info modal -->
    <v-dialog v-model="showReleaseInfo">
      <ncl-release-modal
        :releaseDate="currentDayRobust"
        :release="releaseInfo"
        @close="showReleaseInfo = false"
      />
    </v-dialog>

    <!-- model info modal -->
    <v-dialog activator="#models-info">
      <template v-slot="{ isActive }">
        <ncl-model-modal @close="isActive.value = false" />
      </template>
    </v-dialog>
  </div>
</template>

<script>
import { Chart } from "highcharts-vue";
import { mapActions, mapState, mapStores } from "pinia";
import { usePlausible } from "v-plausible/vue";

import ForecastHistory from "@/components/ForecastHistory.vue";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import NclFlag from "@/components/NclFlag.vue";
import NclInfoIcon from "@/components/NclInfoIcon.vue";
import NclModelModal from "@/components/NclModelModal.vue";
import NclReleaseModal from "@/components/NclReleaseModal.vue";
import ReleaseTable from "@/components/ReleaseTable.vue";
import { useHighchartsTheme } from "@/composables/useHighchartsTheme";
import { useCountryStore } from "@/stores/country";
import { useDetailStore } from "@/stores/detail";
import { dateToQuartal, formatDate } from "@/utils";

export default {
  setup() {
    const { trackPageview, trackEvent } = usePlausible();
    const { isDark, getHighchartsTheme } = useHighchartsTheme();
    return { trackPageview, trackEvent, isDark, getHighchartsTheme };
  },
  mounted() {
    this.trackPageview({
      url: this.getTrackableUrl(),
    });
  },
  head() {
    return {
      title: `${this.country.toUpperCase()} ${this.indicatorUpperCaseName} ${
        this.targetPeriod
      }`,
      ...(this.countryIcon
        ? { link: [{ rel: "icon", href: this.countryIcon }] }
        : {}),
    };
  },
  components: {
    ReleaseTable,
    highcharts: Chart,
    ForecastHistory,
    LoadingOverlay,
    NclFlag,
    NclInfoIcon,
    NclReleaseModal,
    NclModelModal,
  },
  data: function () {
    const currentModel = this.$route.query.model;
    const baseDataModels = {
      all: "Pooled",
      bridge_excl_covid: "Bridge",
      amidas_excl_covid: "MIDAS",
      umidas_excl_covid: "U-MIDAS",
      dfm_excl_covid: "DFM",
      "mf-esn": "MF-ESN",
      randomwalk: "Random Walk",
      rollmean: "Rolling Mean",
      ar_iterative_bic: "Iterative AR",
      arima: "ARIMA",
      ar_direct_bic: "Direct AR",
    };
    const dataModels = !currentModel
      ? baseDataModels
      : Object.keys(baseDataModels).includes(currentModel)
        ? baseDataModels
        : { ...baseDataModels, [currentModel]: currentModel };
    return {
      country: this.$route.query.country,
      countryIcon: null,
      targetVariable: this.$route.query.targetvariable,
      targetPeriod: this.$route.query.targetperiod,
      model: this.$route.query.model || "all",
      isLoading: true,
      dataModels,
      impactCategoriesActive: [],
      activeCategories: [],
      showReleaseInfo: false,
      releaseInfo: {
        title: null,
        description: null,
        category: null,
        color: null,
        source: null,
        unit: null,
        previousValue: null,
        actualValue: null,
        referencePeriod: null,
        impact: null,
      },
    };
  },
  beforeUpdate() {
    // Reset chart pointers before Vue triggers options updates to avoid
    // stale series/point references causing Highcharts errors
    for (const refName of ["histogramChart", "historicSeriesChart"]) {
      const chart = this.$refs[refName]?.chart;
      if (chart?.pointer) {
        try {
          chart.pointer.reset(false, 0);
        } catch {
          // ignore
        }
      }
    }
  },
  methods: {
    ...mapActions(useDetailStore, [
      "loadHistogramData",
      "loadGrowthHistoryData",
      "loadMeasuresData",
      "loadReleaseData",
      "setActiveDay",
    ]),
    async loadIcon() {
      const icon = await import(`../assets/icons/${this.country}.png?url`);
      this.countryIcon = icon.default;
    },
    async loadData(onlyWithDate = false) {
      const promises = [
        this.loadHistogramData({
          country: this.country,
          targetVariable: this.targetVariable,
          targetPeriod: this.targetPeriod,
          model: this.dataModel,
          date: this.currentDaySelected,
        }),
        this.loadReleaseData({
          country: this.country,
          targetVariable: this.targetVariable,
          targetPeriod: this.targetPeriod,
          model: this.dataModel,
          date: this.currentDaySelected,
        }),
      ];
      if (onlyWithDate) {
        return await Promise.all(promises);
      }
      return await Promise.all([
        ...promises,
        this.loadMeasuresData({
          country: this.country,
          targetVariable: this.targetVariable,
          targetPeriod: this.targetPeriod,
          model: this.dataModel,
        }),
        this.loadGrowthHistoryData({
          country: this.country,
          targetVariable: this.targetVariable,
          model: this.dataModel,
        }),
        // Load country store data for the trend calculation
        this.countryStore.loadData(this.targetPeriod),
      ]);
    },
    onCurrentDateTimeChange(val) {
      if (!val) {
        return;
      }
      this.setActiveDay(val.x);
    },
    onActiveCategoriesChange(newActiveCategories) {
      this.activeCategories = newActiveCategories;
    },
    tableRowClick(index, releaseData) {
      const releasesForDate = this.releaseTableData[
        this.currentDaySelected
      ].toSorted((a, b) => Math.abs(b.impact) - Math.abs(a.impact));
      const release = releasesForDate[index];
      const releaseCategoryName = release.category;
      const releaseCategoryColor =
        this.activeCategories[
          this.activeCategories.findIndex(
            (obj) => obj.name === releaseCategoryName,
          )
        ].color;
      this.releaseInfo = {
        title: release.title,
        description: releaseData.description,
        category: releaseCategoryName,
        color: releaseCategoryColor,
        source: release.source ? release.source : "No source",
        unit: release.unit ? release.unit : "No unit",
        previousValue: release.previousValue
          ? Number.parseFloat(release.previousValue).toFixed(2)
          : "No previous value",
        actualValue: release.actualValue
          ? Number.parseFloat(release.actualValue).toFixed(2)
          : "No current value",
        referencePeriod: release.referencePeriod
          ? release.referencePeriod
          : "No reference period",
        impact: release.impact
          ? Number.parseFloat(release.impact).toPrecision(2)
          : 0,
      };
      this.showReleaseInfo = true;
    },
    // Adds query parameters as part of the path so plausible
    // tracks them. By default it discards all query params
    // for data protection reasons.
    getTrackableUrl() {
      const params = ["country", "targetvariable", "targetperiod"];
      const url = new URL(location.href);
      const queryParams = new URLSearchParams(location.search);
      let customUrl = url.protocol + "//" + url.hostname + url.pathname;
      for (const paramName of params) {
        const paramValue = queryParams.get(paramName);
        if (paramValue) customUrl = customUrl + "/" + paramValue;
      }
      return encodeURI(customUrl);
    },
  },
  computed: {
    ...mapState(useCountryStore, ["areas"]),
    ...mapStores(useDetailStore, useCountryStore),
    ...mapState(useDetailStore, [
      "getCurrentForecast",
      "getHistogramData",
      "getGrowthHistoryData",
      "getGrowthHistoryForecastData",
      "getCurrentRMSFE",
      "getCurrentLowerBound",
      "getCurrentUpperBound",
      "getCurrentBias",
      "getOutputData",
      "getCurrentTrend",
    ]),
    ...mapState(useDetailStore, {
      currentDaySelected: "getActiveDay",
      releaseTableData: "getReleaseData",
    }),
    modelSelectItems() {
      return Object.entries(this.dataModels).map(([value, title]) => ({
        value,
        title,
      }));
    },
    dataModel: {
      get() {
        return this.model;
      },
      set(value) {
        this.trackEvent("modelselect", {
          props: {
            model: value,
            country: this.country,
            targetVariable: this.targetVariable,
            targetPeriod: this.targetPeriod,
            indicator: this.$route.query.indicator ?? "en",
          },
        });
        this.model = value;
        this.detailStore.model = value;
        this.setActiveDay(0);
        this.loadData();
        this.$router.push({
          name: this.$route.name,
          params: this.$route.params,
          query: { ...this.$route.query, model: value },
        });
      },
    },
    historicSeriesOptions() {
      const theme = this.getHighchartsTheme(this.isDark);

      let options = {
        ...theme,
        chart: {
          ...theme.chart,
        },
        title: {
          ...theme.title,
          text: "",
          style: {
            ...theme.title.style,
            fontWeight: "bold",
          },
        },
        xAxis: {
          ...theme.xAxis,
          type: "datetime",
          title: {
            ...theme.xAxis.title,
            text: "Date of observation",
          },
          dateTimeLabelFormats: {
            month: "%b %Y",
            year: "%b %Y",
          },
        },
        yAxis: {
          ...theme.yAxis,
          title: {
            ...theme.yAxis.title,
            text: "Percentage (%)",
          },
        },
        tooltip: {
          ...theme.tooltip,
          shared: true,
          formatter: function () {
            let date = new Date(this.x);
            let quartal = dateToQuartal(date);
            return this.points.reduce(
              function (s, point) {
                return (
                  s +
                  "<br/>" +
                  point.series.name +
                  ": " +
                  point.y.toFixed(2) +
                  "%"
                );
              },
              "<b>Q" + quartal + " " + date.getFullYear() + "</b>",
            );
          },
        },
        series: [
          {
            showInLegend: false,
            data: [],
          },
          {
            showInLegend: false,
            lineWidth: 0,
            marker: {
              enabled: true,
              radius: 5,
            },
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
            data: [],
          },
        ],
        plotOptions: {
          line: {
            marker: {
              enabled: false,
            },
          },
        },
      };

      if (this.targetVariable === "gdp") {
        options.yAxis.title.text = "Quarter-on-quarter growth in %";
      }

      options.series[0].data = this.getGrowthHistoryData;
      options.series[1].data = this.getGrowthHistoryForecastData;

      options.series[0].name =
        this.targetVariable === "gdp"
          ? this.indicatorUpperCaseName + " growth"
          : this.indicatorUpperCaseName;
      options.series[1].name =
        this.targetVariable === "gdp"
          ? this.indicatorUpperCaseName + " growth forecast"
          : this.indicatorUpperCaseName;

      return options;
    },
    histogramOptions() {
      const theme = this.getHighchartsTheme(this.isDark);

      let options = {
        ...theme,
        chart: {
          ...theme.chart,
        },
        title: {
          ...theme.title,
          text: "",
          style: {
            ...theme.title.style,
            fontWeight: "bold",
          },
        },
        xAxis: {
          ...theme.xAxis,
          title: {
            ...theme.xAxis.title,
            text: "Bin Mean",
          },
          format: "{value:.2f}",
        },
        yAxis: {
          ...theme.yAxis,
          title: {
            ...theme.yAxis.title,
            text: "Frequency of forecasts (per bin)",
          },
        },
        tooltip: {
          ...theme.tooltip,
          formatter: function () {
            return (
              "Bin Mean: " +
              "<b>" +
              this.point.x.toFixed(2) +
              "%</b><br>" +
              "Forecast frequency: " +
              "<b>" +
              this.point.y.toFixed(0) +
              "</b>"
            );
          },
        },
        series: [
          {
            type: "column",
            pointPlacement: "between",
            showInLegend: false,
            data: [],
          },
        ],
        lang: {
          noData: "No data to display",
        },
        noData: {
          style: {
            fontFamily: "IBM Plex Sans",
            fontWeight: "bold",
            fontSize: "15px",
            color: this.isDark ? "#E0E0E0" : "#303030",
          },
        },
        plotOptions: {
          column: {
            pointPadding: 0,
            borderWidth: 1,
            groupPadding: 0,
          },
        },
      };

      if (this.targetVariable === "gdp") {
        options.xAxis.title.text =
          "Quarter-on-quarter growth forecast in % (bin mean)";
      }

      options.series[0].data =
        this.getHistogramData[this.currentDaySelected] || [];

      return options;
    },
    historicSeriesChartTitle() {
      if (this.targetVariable === "gdp") {
        return `${this.indicatorUpperCaseName} Growth: Data History Including Forecasts`;
      }
      return `${this.indicatorUpperCaseName} Growth: Data History Including Forecasts`;
    },
    currentDayRobust() {
      return this.formatSelectedDay ? this.formatSelectedDay : "Unknown";
    },
    currentForecast() {
      return this.getCurrentForecast?.toFixed(2);
    },
    forecastType() {
      if (this.targetVariable === "gdp") {
        return `Growth Forecast as of ${this.currentDayRobust}`;
      }
      return "";
    },
    forecastTooltip() {
      if (this.targetVariable === "gdp") {
        return `Forecast for ${this.countryAdjective} quarter-on-quarter ${this.indicatorUpperCaseName} growth ${this.targetPeriod} in % as of ${this.currentDayRobust}`;
      }
      return "";
    },
    countryName() {
      let countryObject = this.areas.find((ele) => ele.code === this.country);
      return countryObject.name;
    },
    countryAdjective() {
      let countryAdj = "";
      if (this.country !== "ea_agg") {
        let countryObject = this.areas.find((ele) =>
          ele.countries.includes(this.country.toUpperCase()),
        );
        countryAdj = countryObject.adjective;
      }
      return countryAdj;
    },
    indicatorUpperCaseName() {
      if (this.country == "ie") {
        return "MDD";
      }
      return this.targetVariable.toUpperCase();
    },
    formatSelectedDay() {
      if (this.currentDaySelected) {
        return formatDate(this.currentDaySelected);
      }
      return "";
    },
    indicatorValuePreviousDay() {
      let index = this.getOutputData.findIndex(
        (obj) => obj.x === this.currentDaySelected,
      );
      if (index > 0) {
        let previousDay = this.getOutputData[index - 1];
        return previousDay.y.toFixed(2);
      }
      return null;
    },
    indicatorValueDifferenceToPreviousDay() {
      if (
        this.indicatorValuePreviousDay !== null &&
        this.currentForecast !== null
      ) {
        return (this.currentForecast - this.indicatorValuePreviousDay).toFixed(
          2,
        );
      }
      return null;
    },
    currentRMSFE() {
      let data = this.getCurrentRMSFE;
      if (data) {
        return data.toFixed(2);
      }
      return null;
    },
    currentLowerBound() {
      let data = this.getCurrentLowerBound;
      if (data) {
        return data.toFixed(2);
      }
      return null;
    },
    currentUpperBound() {
      let data = this.getCurrentUpperBound;
      if (data) {
        return data.toFixed(2);
      }
      return null;
    },
    currentBias() {
      let data = this.getCurrentBias;
      if (data) {
        return data.toFixed(2);
      }
      return null;
    },
  },
  async created() {
    this.isLoading = true;

    // Ensure quarters are loaded (may already be loaded by App.vue)
    if (this.countryStore.quarters.length === 0) {
      await this.countryStore.loadQuarters();
    }

    // Initialize store from URL params
    const country = this.$route.query.country;
    if (country) {
      const countryObj = this.areas.find((area) => area.code === country);
      if (countryObj) {
        this.countryStore.activeCountry = countryObj;
      }
    }

    // Always sync model to ensure store and component are in sync
    const model = this.$route.query.model || "all";
    this.detailStore.model = model;

    await Promise.all([this.loadIcon(), this.loadData()]);
    this.isLoading = false;
  },
  watch: {
    currentDaySelected(newVal) {
      if (!newVal) return;
      this.loadData(true);
    },
    "$route.query": {
      async handler(newQuery, oldQuery) {
        // Only reload if relevant params changed
        if (
          newQuery.country !== oldQuery.country ||
          newQuery.targetperiod !== oldQuery.targetperiod ||
          newQuery.targetvariable !== oldQuery.targetvariable ||
          newQuery.model !== oldQuery.model
        ) {
          this.isLoading = true;

          // Update local data
          this.country = newQuery.country;
          this.targetPeriod = newQuery.targetperiod;
          this.targetVariable = newQuery.targetvariable;
          this.model = newQuery.model || "all";

          // Update store
          if (newQuery.country) {
            const countryObj = this.areas.find(
              (area) => area.code === newQuery.country,
            );
            if (countryObj) {
              this.countryStore.activeCountry = countryObj;
            }
          }

          // Always sync model to ensure store and component are in sync
          const newModel = newQuery.model || "all";
          this.detailStore.model = newModel;

          // Reset active day so stale selection doesn't flash for new config
          this.setActiveDay(0);

          // Reload data
          await Promise.all([this.loadIcon(), this.loadData()]);
          this.isLoading = false;
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.no-wrap {
  white-space: nowrap;
}

.box {
  padding: 0.75rem;
}

hr {
  margin-top: 0;
}

.button:focus {
  border-color: rgb(219, 219, 219);
}

.button:focus:not(:active),
.button.is-focused:not(:active) {
  box-shadow: none;
}

.button-label {
  margin-right: 1rem;
}

.button.is-primary,
.button.is-primary:hover,
.button.is-primary:active {
  background-color: #7cb5ec;
}

.button.is-primary:focus:not(:active),
.button.is-primary.is-focused:not(:active) {
  box-shadow: none;
}

.text-not-applicable {
  font-weight: bold;
  text-align: center;
}

.model-select-mobile {
  margin-top: 0.5rem;
  min-width: 240px;
  max-width: 100%;

  .v-field__append-inner {
    display: flex !important;
    opacity: 1 !important;
  }
}

// Flex items default to min-width: auto, so a rendered Highcharts SVG keeps
// its column from ever shrinking again (window shrink / browser zoom) —
// chart and column hold each other wide. min-width: 0 breaks the deadlock
// and lets Highcharts reflow down.
.columns > .column {
  min-width: 0;
}

// Model selector: Bulma's .level-item is flex-shrink: 0, so the chip row
// pushes past the viewport instead of letting the chips wrap (v-chip-group
// has the column prop for that).
.columns > .level-item {
  flex-shrink: 1;
  min-width: 0;
  max-width: 100%;
}

@media screen and (min-width: 769px) and (max-width: 1407px) {
  // Stack Forecast History and Forecast Distribution vertically —
  // the 3/5 + 2/5 split only has room from widescreen (1408px) up.
  // Use explicit 100% basis so Highcharts can compute a real width
  // (flex: none collapses to content width and the chart disappears).
  // Bulma .columns is nowrap by default — without wrap the two 100%
  // columns sit side by side and overflow.
  .row > .columns {
    flex-wrap: wrap;
  }

  .columns > .column.is-three-fifths,
  .columns > .column.is-two-fifths {
    flex: 0 0 100%;
    width: 100%;
    max-width: 100%;
  }
}

@media screen and (min-width: 769px) and (max-width: 1023px) {
  // Metric boxes (Forecast / Lower / Upper / Trend / RMSFE / Bias) —
  // show 2 per row instead of stacking 1-per-row on iPad portrait.
  .columns.is-multiline > .column {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

@media screen and (min-width: 1024px) and (max-width: 1407px) {
  // Step between 2-up (tablet) and 6-up (widescreen): 3 metric boxes per row.
  .columns.is-multiline > .column {
    flex: 0 0 33.3333%;
    max-width: 33.3333%;
  }
}

@media screen and (max-width: 768px) {
  // Same 2-up grid for metric boxes on phone/tablet portrait.
  // Force flex because Bulma's .columns is display: block below 769px.
  .columns.is-multiline {
    display: flex;
    flex-wrap: wrap;
  }

  .columns.is-multiline > .column {
    flex: 0 0 50%;
    max-width: 50%;
  }
}
</style>
